import {
  CBB_TEAM_NAMES,
  CFB_TEAM_NAMES,
  MLB_TEAM_NAMES,
  NBA_TEAM_NAMES,
  NFL_TEAM_NAMES,
  NHL_TEAM_NAMES,
  getTeamDisplayName,
} from './logoMaps.js'

const COLLEGE_TEAM_NAMES = {
  ...CFB_TEAM_NAMES,
  ...CBB_TEAM_NAMES,
}

const CBB_TEAM_CODE_ALIASES = {
  ALABAMA: 'ALA',
  ALABAMACRIMSONTIDE: 'ALA',
  AMERICAN: 'AME',
  ARZ: 'ARIZ',
  ARIZONA: 'ARIZ',
  ARIZONAWILDCATS: 'ARIZ',
  ARKANSAS: 'ARK',
  ARKANSASRAZORBACKS: 'ARK',
  CONN: 'UCONN',
  CONNECTICUT: 'UCONN',
  CONNECTICUTHUSKIES: 'UCONN',
  DUKEBLUEDEVILS: 'DUKE',
  HOUSTON: 'HOU',
  HOUSTONCOUGARS: 'HOU',
  ILLINOIS: 'ILL',
  ILLINOISFIGHTINGILLINI: 'ILL',
  IOWAHAWKEYES: 'IOWA',
  IOWASTATE: 'ISU',
  IOWASTATECYCLONES: 'ISU',
  MICHIGANSTATE: 'MSU',
  MICHIGANSTATESPARTANS: 'MSU',
  MICHIGANWOLVERINES: 'MICH',
  MINNESOTAGOLDENGOPHERS: 'MINN',
  NEBRASKA: 'NEB',
  NEBRASKACORNHUSKERS: 'NEB',
  NORTHCAROLINA: 'UNC',
  NORTHCAROLINATARHEELS: 'UNC',
  NOTREDAME: 'ND',
  NOTREDAMEFIGHTINGI: 'ND',
  NOTREDAMEFIGHTINGIRISH: 'ND',
  PURDUEBOILERMAKERS: 'PUR',
  PURDUE: 'PUR',
  SAINTJOHN: 'STJN',
  SAINTJOHNS: 'STJN',
  SAINTJOHNSREDSTORM: 'STJN',
  SJU: 'STJN',
  STJOHN: 'STJN',
  STJOHNS: 'STJN',
  STJOHNSSREDSTORM: 'STJN',
  TENNESSEE: 'TENN',
  TENNESSEEVOLUNTEERS: 'TENN',
  TEXAS: 'TEX',
  TEXASLONGHORNS: 'TEX',
  UCON: 'UCONN',
  UCONN: 'UCONN',
  UH: 'HOU',
  UNC: 'UNC',
  UT: 'TEX',
  WESTVIRGINIA: 'WVU',
  WESTVIRGINIAMOUNTAINEERS: 'WVU',
}

const TEAM_CODE_ALIASES = {
  NBA: {
    GS: 'GSW',
    NO: 'NOP',
    NOPELICANS: 'NOP',
    NY: 'NYK',
    NYKNICKS: 'NYK',
    PHO: 'PHX',
    PHXSUNS: 'PHX',
    SA: 'SAS',
    SASPURS: 'SAS',
    UTAH: 'UTA',
    GSWARRIORS: 'GSW',
    WSH: 'WAS',
    WASWIZARDS: 'WAS',
  },
  NHL: {
    LA: 'LAK',
    NJ: 'NJD',
    TB: 'TBL',
    WSH: 'WSH',
  },
  MLB: {
    BALORIOLES: 'BAL',
    ATHLETICS: 'OAK',
    CHICUBS: 'CHC',
    KAN: 'KCR',
    KANSASCITY: 'KCR',
    KANSASCITYROYALS: 'KCR',
    KC: 'KCR',
    CHIWHITESOX: 'CHW',
    CHICAGOCUBS: 'CHC',
    CHICAGOWHITESOX: 'CHW',
    COLROCKIES: 'COL',
    DETTIGERS: 'DET',
    HOUASTROS: 'HOU',
    LAANGELS: 'LAA',
    MILBREWERS: 'MIL',
    MIAMARLINS: 'MIA',
    MINTWINS: 'MIN',
    NYMETS: 'NYM',
    NYYANKEES: 'NYY',
    OAKLANDATHLETICS: 'OAK',
    PHIPHILLIES: 'PHI',
    PITPIRATES: 'PIT',
    SF: 'SFG',
    SFGIANTS: 'SFG',
    SFO: 'SFG',
    SD: 'SDP',
    SDPADRES: 'SDP',
    TEXRANGERS: 'TEX',
    STLOUISCARDINALS: 'STL',
    STLCARDINALS: 'STL',
    TB: 'TBR',
    TBRAYS: 'TBR',
    TAMPABAYRAYS: 'TBR',
    THEATHLETICS: 'OAK',
    WAS: 'WSH',
    WASNATIONALS: 'WSH',
    WASHINGTONNATIONALS: 'WSH',
    WSN: 'WSH',
  },
  NFL: {
    WSH: 'WAS',
  },
  CBB: CBB_TEAM_CODE_ALIASES,
  NCAAB: CBB_TEAM_CODE_ALIASES,
  NCAAM: CBB_TEAM_CODE_ALIASES,
  NCAAW: CBB_TEAM_CODE_ALIASES,
}

const TEAM_NAME_MAPS = {
  NFL: NFL_TEAM_NAMES,
  NBA: NBA_TEAM_NAMES,
  NHL: NHL_TEAM_NAMES,
  MLB: MLB_TEAM_NAMES,
  CBB: COLLEGE_TEAM_NAMES,
  NCAAB: COLLEGE_TEAM_NAMES,
  NCAAM: COLLEGE_TEAM_NAMES,
  NCAAW: COLLEGE_TEAM_NAMES,
  CFB: CFB_TEAM_NAMES,
  NCAAF: CFB_TEAM_NAMES,
}

function buildTeamNameAliases(nameMap) {
  const aliases = {}
  Object.entries(nameMap || {}).forEach(([code, displayName]) => {
    const compactCode = compactTeamKey(code)
    const compactDisplay = compactTeamKey(displayName)
    if (compactDisplay) aliases[compactDisplay] = code

    const words = String(displayName || '').split(/\s+/).filter(Boolean)
    if (words.length >= 1) {
      const firstWord = compactTeamKey(words[0])
      const lastWord = compactTeamKey(words.slice(-1).join(' '))
      const lastTwoWords = compactTeamKey(words.slice(-2).join(' '))
      const firstTwoWords = compactTeamKey(words.slice(0, 2).join(' '))
      const firstWordShort = compactTeamKey(words[0]).slice(0, 3)

      if (firstWord && lastWord) aliases[`${firstWord}${lastWord}`] = code
      if (firstTwoWords && lastWord) aliases[`${firstTwoWords}${lastWord}`] = code
      if (compactCode && lastWord) aliases[`${compactCode}${lastWord}`] = code
      if (compactCode && lastTwoWords) aliases[`${compactCode}${lastTwoWords}`] = code
      if (firstWordShort && lastWord) aliases[`${firstWordShort}${lastWord}`] = code
      if (firstWordShort && lastTwoWords) aliases[`${firstWordShort}${lastTwoWords}`] = code
    }
  })
  return aliases
}

const TEAM_NAME_ALIASES = Object.fromEntries(
  Object.entries(TEAM_NAME_MAPS).map(([league, nameMap]) => [league, buildTeamNameAliases(nameMap)])
)

const TEAM_CODE_LOOKUPS = Object.fromEntries(
  Object.entries(TEAM_NAME_MAPS).map(([league, nameMap]) => [
    league,
    new Set(Object.keys(nameMap || {}).map((code) => compactTeamKey(code)).filter(Boolean)),
  ])
)

const TEAM_NAME_ALIAS_KEYS = Object.fromEntries(
  Object.entries(TEAM_NAME_ALIASES).map(([league, aliases]) => [
    league,
    Object.keys(aliases).sort((left, right) => right.length - left.length),
  ])
)

const NON_RENDERABLE_TEAM_TOKENS = new Set(['', 'UNKNOWN', 'YES', 'NO', 'OVER', 'UNDER'])
const NON_TEAM_MARKETS = new Set(['ML', 'SPREAD', 'TOTAL'])
const FUTURES_KEYWORDS = [
  'division',
  'conference',
  'league',
  'winner',
  'champion',
  'championship',
  'mvp',
  'rookie',
  'sixth',
  'award',
  'futures',
  'playoff',
  'playoffs',
  'series',
  'pennant',
  'world series',
  'cy young',
]

const BOARD_MARKET_LIMITS = {
  MLB: {
    SPREAD: { min: -6.5, max: 6.5 },
    TOTAL: { min: 3, max: 25 },
  },
  NHL: {
    SPREAD: { min: -6.5, max: 6.5 },
    TOTAL: { min: 2, max: 15 },
  },
  NBA: {
    SPREAD: { min: -45.5, max: 45.5 },
    TOTAL: { min: 100, max: 350 },
  },
  NFL: {
    SPREAD: { min: -60.5, max: 60.5 },
    TOTAL: { min: 10, max: 100 },
  },
  NCAAF: {
    SPREAD: { min: -60.5, max: 60.5 },
    TOTAL: { min: 10, max: 100 },
  },
  CFB: {
    SPREAD: { min: -60.5, max: 60.5 },
    TOTAL: { min: 10, max: 100 },
  },
  NCAAB: {
    SPREAD: { min: -45.5, max: 45.5 },
    TOTAL: { min: 80, max: 220 },
  },
  NCAAW: {
    SPREAD: { min: -45.5, max: 45.5 },
    TOTAL: { min: 80, max: 220 },
  },
  CBB: {
    SPREAD: { min: -45.5, max: 45.5 },
    TOTAL: { min: 80, max: 220 },
  },
  SOCCER: {
    SPREAD: { min: -6.5, max: 6.5 },
    TOTAL: { min: 0.5, max: 10.5 },
  },
  MMA: {
    SPREAD: { min: -10.5, max: 10.5 },
    TOTAL: { min: 0.5, max: 10.5 },
  },
  TENNIS: {
    SPREAD: { min: -10.5, max: 10.5 },
    TOTAL: { min: 10, max: 80 },
  },
}

function normalizeDigits(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  if (Number.isInteger(numeric)) return String(numeric)
  return String(Number(numeric.toFixed(3)))
}

export function normalizeText(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

export function normalizeLeagueCode(league) {
  const normalized = String(league || '').toUpperCase()
  if (normalized === 'NCAAM') return 'NCAAB'
  if (normalized === 'CBB') return 'NCAAB'
  if (normalized === 'CFB') return 'NCAAF'
  if (normalized === 'UFC') return 'MMA'
  return normalized
}

export function extractEventDateKey(canonicalId, startTime) {
  if (startTime) {
    const date = new Date(startTime)
    if (!Number.isNaN(date.getTime())) {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      return formatter.format(date).replace(/-/g, '')
    }
  }

  const match = String(canonicalId || '').match(/^[A-Z0-9]+_(\d{8})_/)
  if (match) return match[1]

  return 'unknown'
}

function compactTeamKey(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

function buildTeamKeyCandidates(rawValue) {
  const compact = compactTeamKey(rawValue)
  if (!compact) return []

  const candidates = new Set([compact])
  const withoutSeed = compact.replace(/^\d{1,2}/, '')
  if (withoutSeed) candidates.add(withoutSeed)

  const withoutArticle = withoutSeed.replace(/^THE(?=[A-Z])/, '')
  if (withoutArticle) candidates.add(withoutArticle)

  return Array.from(candidates).filter(Boolean)
}

export function normalizeTeamCode(teamCode, league) {
  const normalizedLeague = normalizeLeagueCode(league)
  const raw = String(teamCode || '').trim()
  if (!raw) return ''

  const aliasMap = TEAM_CODE_ALIASES[normalizedLeague] || {}
  const displayAliases = TEAM_NAME_ALIASES[normalizedLeague] || {}
  const aliasKeys = TEAM_NAME_ALIAS_KEYS[normalizedLeague] || []
  const codeLookup = TEAM_CODE_LOOKUPS[normalizedLeague] || new Set()

  for (const candidate of buildTeamKeyCandidates(raw)) {
    const alias = aliasMap[candidate]
    if (alias) return alias

    if (codeLookup.has(candidate)) return candidate

    const displayAlias = displayAliases[candidate]
    if (displayAlias) return displayAlias

    if (candidate.length > 3) {
      const prefixAlias = aliasKeys.find((key) => candidate.startsWith(key) || key.startsWith(candidate))
      if (prefixAlias) return displayAliases[prefixAlias]
    }
  }

  const compact = compactTeamKey(raw)
  return compact || raw.toUpperCase()
}

function isKnownTeamCode(teamCode, league) {
  const normalizedLeague = normalizeLeagueCode(league)
  const codeLookup = TEAM_CODE_LOOKUPS[normalizedLeague]
  if (!codeLookup || codeLookup.size === 0) return true
  return codeLookup.has(normalizeTeamCode(teamCode, normalizedLeague))
}

function isReasonableBoardLine(row) {
  const market = String(row?.market_normalized || '').toUpperCase()
  if (market === 'ML') return true
  if (market !== 'SPREAD' && market !== 'TOTAL') return false

  const numericHandicap = Number(row?.handicap)
  if (!Number.isFinite(numericHandicap)) return false

  const normalizedLeague = normalizeLeagueCode(row?.league)
  const limits = BOARD_MARKET_LIMITS[normalizedLeague]?.[market]
  if (!limits) return true

  return numericHandicap >= limits.min && numericHandicap <= limits.max
}

export function getNormalizedTeamDisplay(teamCode, league) {
  const normalizedCode = normalizeTeamCode(teamCode, league)
  return getTeamDisplayName(normalizedCode, league) || String(teamCode || '')
}

function getTeamTokens(teamCode, league) {
  const normalizedCode = normalizeTeamCode(teamCode, league)
  const displayName = getNormalizedTeamDisplay(normalizedCode, league)
  const lastWord = displayName.split(' ').slice(-1).join(' ')

  return new Set(
    [normalizedCode, displayName, lastWord]
      .map((value) => normalizeText(value))
      .filter(Boolean)
  )
}

export function selectionMatchesTeam(selection, teamCode, league) {
  const normalizedSelection = normalizeText(selection)
  if (!normalizedSelection) return false

  const tokens = getTeamTokens(teamCode, league)
  if (tokens.has(normalizedSelection)) return true

  return Array.from(tokens).some((token) => token && normalizedSelection.includes(token))
}

export function classifyTotalSelection(selection) {
  const normalized = normalizeText(selection)
  if (!normalized) return null
  if (normalized === 'over' || normalized.startsWith('over ') || normalized.includes(' over')) return 'over'
  if (normalized === 'under' || normalized.startsWith('under ') || normalized.includes(' under')) return 'under'
  if (normalized.startsWith('o ')) return 'over'
  if (normalized.startsWith('u ')) return 'under'
  return null
}

export function normalizeSelectionLabel(row) {
  const market = String(row.market_normalized || '').toUpperCase()
  const awayTeam = normalizeTeamCode(row.away_team, row.league)
  const homeTeam = normalizeTeamCode(row.home_team, row.league)

  if (market === 'ML' || market === 'SPREAD') {
    if (selectionMatchesTeam(row.selection, awayTeam, row.league)) return awayTeam
    if (selectionMatchesTeam(row.selection, homeTeam, row.league)) return homeTeam
  }

  if (market === 'TOTAL') {
    const side = classifyTotalSelection(row.selection)
    if (side === 'over') return 'Over'
    if (side === 'under') return 'Under'
  }

  return String(row.selection || '').trim()
}

function inferBoardMarketNormalized(row, awayTeam, homeTeam) {
  const rawMarket = String(row?.market_normalized || '').toUpperCase()
  if (rawMarket === 'ML' || rawMarket === 'SPREAD' || rawMarket === 'TOTAL') {
    return rawMarket
  }

  if (selectionMatchesTeam(row?.selection, awayTeam, row?.league) || selectionMatchesTeam(row?.selection, homeTeam, row?.league)) {
    const handicap = Number(row?.handicap)
    return Number.isFinite(handicap) && handicap !== 0 ? 'SPREAD' : 'ML'
  }

  const totalSide = classifyTotalSelection(row?.selection)
  if (totalSide) {
    const handicap = Number(row?.handicap)
    if (Number.isFinite(handicap)) return 'TOTAL'
  }

  return rawMarket
}

function containsFuturesKeyword(value) {
  const normalized = normalizeText(value)
  if (!normalized) return false
  return FUTURES_KEYWORDS.some((keyword) => normalized.includes(keyword))
}

function looksRenderableTeam(rawTeam) {
  const compact = compactTeamKey(rawTeam)
  if (!compact) return false
  if (NON_RENDERABLE_TEAM_TOKENS.has(compact)) return false
  if (containsFuturesKeyword(rawTeam)) return false
  if (/\d{4}/.test(compact)) return false
  return true
}

export function isRenderableTeamOddsRow(row) {
  if (!row) return false

  const market = String(row.market_normalized || '').toUpperCase()
  if (!NON_TEAM_MARKETS.has(market)) return false

  const awayTeam = String(row.away_team || '').trim()
  const homeTeam = String(row.home_team || '').trim()
  if (!looksRenderableTeam(awayTeam) || !looksRenderableTeam(homeTeam)) return false
  if (!isKnownTeamCode(awayTeam, row.league) || !isKnownTeamCode(homeTeam, row.league)) return false
  if (/[?]/.test(awayTeam) || /[?]/.test(homeTeam) || /[?]/.test(String(row.participants || ''))) return false
  if (normalizeText(row.selection) === 'yes' || normalizeText(row.selection) === 'no') return false
  if (normalizeText(awayTeam).includes('winner') || normalizeText(homeTeam).includes('winner')) return false

  if (market !== 'TOTAL') {
    const selectionCompact = compactTeamKey(row.selection)
    if (NON_RENDERABLE_TEAM_TOKENS.has(selectionCompact)) return false
  }

  if (!isReasonableBoardLine(row)) return false

  if (
    containsFuturesKeyword(row.canonical_id) ||
    containsFuturesKeyword(row.participants)
  ) {
    return false
  }

  return true
}

/**
 * Detect insertion timestamps masquerading as start_time.
 * Real game times are rounded to the minute (no fractional seconds, no milliseconds).
 * Insertion timestamps often have fractional seconds or sub-minute precision.
 */
export function isLikelyInsertionTimestamp(startTime) {
  if (!startTime) return false
  const str = String(startTime)
  // Fractional seconds (e.g., "2026-03-27T18:30:05.123Z") → insertion timestamp
  if (/\.\d{3,}/.test(str)) return true
  // Non-zero seconds (e.g., ":47Z" or ":47+00") → likely insertion, real games start at :00
  const secondsMatch = str.match(/:(\d{2})(?:\.\d+)?(?:Z|[+-])/)
  if (secondsMatch && secondsMatch[1] !== '00') return true
  return false
}

export function isWithinOddsWindow(row, minStartMs, maxStartMs) {
  const startMs = row?.start_time ? new Date(row.start_time).getTime() : Number.NaN
  if (!Number.isFinite(startMs)) return false
  return startMs > minStartMs && startMs < maxStartMs
}

export function buildNormalizedEventKey(row) {
  const league = normalizeLeagueCode(row.league)
  const dateKey = extractEventDateKey(row.canonical_id, row.start_time)
  const awayDisplay = getNormalizedTeamDisplay(row.away_team, league)
  const homeDisplay = getNormalizedTeamDisplay(row.home_team, league)

  return [
    league,
    dateKey,
    normalizeText(awayDisplay),
    normalizeText(homeDisplay),
  ].join('|')
}

export function buildNormalizedCanonicalId(row) {
  const league = normalizeLeagueCode(row.league)
  const dateKey = extractEventDateKey(row.canonical_id, row.start_time)
  const awayTeam = normalizeTeamCode(row.away_team, league)
  const homeTeam = normalizeTeamCode(row.home_team, league)
  return `${league}_${dateKey}_${awayTeam}_${homeTeam}`
}

export function buildNormalizedOutcomeKey(row) {
  const market = String(row.market_normalized || '').toUpperCase()
  const handicapValue =
    market === 'ML' && Number(row.handicap) === 0
      ? ''
      : row.handicap == null
        ? ''
        : normalizeDigits(row.handicap)

  return [
    buildNormalizedEventKey(row),
    market,
    normalizeSelectionLabel(row),
    handicapValue,
  ].join('|')
}

const FINAL_STATUS_TOKENS = ['final', 'ended', 'complete', 'completed', 'closed', 'settled']
const PREGAME_STATUS_TOKENS = ['not started', 'not_started', 'notstarted', 'prematch', 'pre match', 'upcoming', 'scheduled', 'open']

const MAX_LIVE_DURATION_HOURS = {
  MLB: 8,
  NBA: 5,
  NHL: 5,
  NFL: 6,
  CFB: 6,
  NCAAF: 6,
  CBB: 5,
  NCAAB: 5,
  NCAAW: 5,
  SOCCER: 4,
  MMA: 8,
  UFC: 8,
  TENNIS: 6,
}

const SUPPORTED_BOARD_LEAGUES = new Set([
  'NFL',
  'NBA',
  'MLB',
  'NHL',
  'CFB',
  'NCAAF',
  'CBB',
  'NCAAB',
  'NCAAM',
  'NCAAW',
  'SOCCER',
  'MMA',
  'UFC',
  'TENNIS',
])

const LIVE_FRESHNESS_MINUTES = 20
const PREMATCH_LIVE_TOLERANCE_MINUTES = 15
const NON_LIVE_PAST_GRACE_MINUTES = 30
const FUTURE_PREGAME_STALENESS_HOURS = 24
const IN_PROGRESS_DISPLAY_STALENESS_MINUTES = 120

export function isFinalStatus(status) {
  const normalized = normalizeText(status)
  return FINAL_STATUS_TOKENS.some((token) => normalized.includes(token))
}

export function isPregameStatus(status) {
  const normalized = normalizeText(status)
  return PREGAME_STATUS_TOKENS.some((token) => normalized.includes(token))
}

export function isEffectivelyLive(row, now = Date.now()) {
  if (!row?.is_live) return false
  if (isFinalStatus(row.game_status)) return false
  if (isPregameStatus(row.game_status)) return false

  const updatedAt = row.updated_at ? new Date(row.updated_at).getTime() : null
  if (updatedAt && now - updatedAt > LIVE_FRESHNESS_MINUTES * 60_000) return false

  const league = normalizeLeagueCode(row.league)
  const maxDurationHours = MAX_LIVE_DURATION_HOURS[league] || 6
  const startTime = row.start_time ? new Date(row.start_time).getTime() : null
  if (startTime && startTime - now > PREMATCH_LIVE_TOLERANCE_MINUTES * 60_000) return false
  if (startTime && now - startTime > maxDurationHours * 3600_000) return false

  return true
}

function hasRecentUpdate(row, now = Date.now(), maxAgeMinutes = IN_PROGRESS_DISPLAY_STALENESS_MINUTES) {
  const updatedAt = row?.updated_at ? new Date(row.updated_at).getTime() : Number.NaN
  if (!Number.isFinite(updatedAt)) return false
  return now - updatedAt <= maxAgeMinutes * 60_000
}

export function hasCurrentGameWindowEvidence(row, now = Date.now()) {
  if (isFinalStatus(row?.game_status)) return false

  const startMs = row?.start_time ? new Date(row.start_time).getTime() : Number.NaN
  if (!Number.isFinite(startMs)) return false

  const league = normalizeLeagueCode(row?.league)
  const maxDurationHours = MAX_LIVE_DURATION_HOURS[league] || 6
  if (startMs > now + PREMATCH_LIVE_TOLERANCE_MINUTES * 60_000) return false
  if (startMs < now - (maxDurationHours + 1) * 3600_000) return false

  return hasRecentUpdate(row, now)
}

export function isBoardVisibleRow(row, now = Date.now(), minStartMs = null, maxStartMs = null) {
  if (!isRenderableTeamOddsRow(row)) return false
  if (!SUPPORTED_BOARD_LEAGUES.has(normalizeLeagueCode(row.league))) return false

  const startMs = row?.start_time ? new Date(row.start_time).getTime() : Number.NaN
  if (!Number.isFinite(startMs)) return false
  const currentGameWindowEvidence = hasCurrentGameWindowEvidence(row, now)
  if (minStartMs != null && startMs <= minStartMs && !currentGameWindowEvidence) return false
  if (maxStartMs != null && startMs >= maxStartMs && !currentGameWindowEvidence) return false
  if (isFinalStatus(row.game_status)) return false

  const effectiveLive = isEffectivelyLive(row, now)
  const updatedAtMs = row?.updated_at ? new Date(row.updated_at).getTime() : Number.NaN
  const league = normalizeLeagueCode(row.league)
  const maxDurationHours = MAX_LIVE_DURATION_HOURS[league] || 6

  if (effectiveLive || currentGameWindowEvidence) {
    return true
  }

  if (!effectiveLive && startMs < now - NON_LIVE_PAST_GRACE_MINUTES * 60_000) {
    return false
  }

  if (!effectiveLive && startMs < now - (maxDurationHours + 1) * 3600_000) {
    return false
  }

  if (
    !effectiveLive &&
    startMs < now - 30 * 60_000 &&
    Number.isFinite(updatedAtMs) &&
    now - updatedAtMs > 90 * 60_000
  ) {
    return false
  }

  if (
    !effectiveLive &&
    Number.isFinite(updatedAtMs) &&
    startMs >= now - NON_LIVE_PAST_GRACE_MINUTES * 60_000 &&
    now - updatedAtMs > FUTURE_PREGAME_STALENESS_HOURS * 3600_000
  ) {
    return false
  }

  return true
}

export function normalizeOddsRow(row) {
  const league = normalizeLeagueCode(row.league)
  const awayTeam = normalizeTeamCode(row.away_team, league)
  const homeTeam = normalizeTeamCode(row.home_team, league)
  const marketNormalized = inferBoardMarketNormalized({ ...row, league }, awayTeam, homeTeam)
  const normalizedSelection = normalizeSelectionLabel({
    ...row,
    league,
    market_normalized: marketNormalized,
    away_team: awayTeam,
    home_team: homeTeam,
  })

  return {
    ...row,
    league,
    away_team: awayTeam,
    home_team: homeTeam,
    market_normalized_original: row.market_normalized,
    market_normalized: marketNormalized,
    canonical_id_normalized: buildNormalizedCanonicalId({
      ...row,
      league,
      away_team: awayTeam,
      home_team: homeTeam,
    }),
    normalized_event_key: buildNormalizedEventKey({
      ...row,
      league,
      away_team: awayTeam,
      home_team: homeTeam,
    }),
    normalized_selection: normalizedSelection,
    is_live_effective: isEffectivelyLive({ ...row, league }),
    source_priority: row?.source_priority ?? 0,
  }
}
